import { Text, Flex, Box } from "@chakra-ui/layout";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { FormattedDistanceToPlacement } from "../../../components/FormattedDistanceToPlacement";
import { BoundaryTag } from "../../../components/BoundaryTag";
import { usePlacementData } from "../../../hooks/usePlacementData";
import { RootState } from "../../../store";
import { ContentItem, KeyInfo } from "../../../types/UIConfig";
import { KeyInfoItem } from "./KeyInfoItem";
import { EligibilityInfoAlert } from "../../../components/Feedback/EligibilityInfoAlert";
import { SpaceProps } from "@chakra-ui/react";
import { isApplicableForEligibilityCheck } from "../../../types/Placement";

export type ProfileInfoSectionProps = {
  placementId: string | undefined;
} & SpaceProps;

export const ProfileInfoSection = ({
  placementId,
  paddingTop,
  paddingBottom,
  paddingX,
}: ProfileInfoSectionProps): React.ReactElement | null => {
  const { getPlacementStringValue, placement } = usePlacementData(placementId);
  const config = useSelector((state: RootState) => state.config);

  if (placementId === undefined) return null;

  if (config.state !== "finished") return null;

  return (
    <Flex
      direction="column"
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      paddingX={paddingX}
    >
      <Flex
        direction="row"
        alignItems="center"
        paddingBottom={{ base: 2, lg: 3 }}
        paddingX={{ base: 1 }}
        wrap="wrap"
        gridColumnGap={3}
        gridRowGap={2}
      >
        <Text fontSize="sm" fontWeight="bold">
          {config.values.profile.highlighted
            .map((item: ContentItem) => getPlacementStringValue(item))
            .join(" | ")}
        </Text>
        <FormattedDistanceToPlacement
          id={placementId}
          renderAtThisAddress={() => (
            <Text fontSize="sm" fontWeight="bold">
              <FormattedMessage id="AT_THIS_ADDRESS" />
            </Text>
          )}
          renderDistance={(miles) => (
            <Flex whiteSpace="nowrap" gridGap="1">
              <Text fontSize="sm">
                <FormattedMessage id="DISTANCE" />:
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {miles}
              </Text>
            </Flex>
          )}
        />
        {placement && <BoundaryTag placement={placement} marginTop={0} />}
      </Flex>

      <Box>
        {config.values.profile.keyInfo.map(
          (keyInfoItem: KeyInfo, index: number) => {
            const placementValue = getPlacementStringValue(keyInfoItem);

            if (placementValue) {
              const key = `${
                keyInfoItem.contentType === "static"
                  ? keyInfoItem.value
                  : keyInfoItem.fieldName
              }${index}`;
              return (
                <KeyInfoItem
                  key={key}
                  keyInfo={keyInfoItem}
                  value={placementValue}
                />
              );
            } else {
              return null;
            }
          }
        )}
      </Box>
      {placement && isApplicableForEligibilityCheck(placement) && (
        <Box paddingTop={{ base: 1, lg: 3 }} paddingRight={{ base: 1, lg: 4 }}>
          <EligibilityInfoAlert>
            {({ checkEligibilityLink }) =>
              checkEligibilityLink(
                config.values.translations?.eligibility.alert
              )
            }
          </EligibilityInfoAlert>
        </Box>
      )}
    </Flex>
  );
};
